import { useEffect, useRef, useState } from 'preact/hooks';
import { cn } from '../../lib/utils';

// Default thumb color: GOLD at ~45% flattened onto the dialog surface
// (#0e0a06). Solid per the no-transparency rule.
const THUMB_COLOR = '#5c4a26';

/**
 * Scroll container with a persistent overlay scrollbar.
 *
 * Native scrollbars are hidden app-wide (index.css) because they consume
 * layout width in Chromium, shifting content left the moment an element
 * becomes scrollable. Most scroll regions get a fade-in/out thumb
 * automatically from utils/overlayScrollbars.js; use this component
 * instead for regions that should show the thumb whenever they overflow
 * (dropdown menus, pickers). It renders its own thumb above the content,
 * so insets stay identical whether or not it scrolls.
 */
function ScrollArea({ className, viewportClassName, thumbColor = THUMB_COLOR, children }) {
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const [thumb, setThumb] = useState(null);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    function update() {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight <= clientHeight + 1) {
        setThumb(null);
        return;
      }
      const height = Math.max(20, (clientHeight / scrollHeight) * clientHeight);
      const maxTop = clientHeight - height;
      const top = maxTop * (scrollTop / (scrollHeight - clientHeight));
      setThumb({ top, height });
    }

    update();
    el.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    // Re-measure when options are filtered in/out of the menu.
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  function handleThumbPointerDown(event) {
    const el = viewportRef.current;
    if (!el || !thumb) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { startY: event.clientY, startScrollTop: el.scrollTop };

    function handleMove(moveEvent) {
      const drag = dragRef.current;
      if (!drag) return;
      const { scrollHeight, clientHeight } = el;
      const trackRange = clientHeight - thumb.height;
      if (trackRange <= 0) return;
      const scrollRange = scrollHeight - clientHeight;
      el.scrollTop = drag.startScrollTop
        + ((moveEvent.clientY - drag.startY) / trackRange) * scrollRange;
    }
    function handleUp() {
      dragRef.current = null;
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    }
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  }

  return (
    // Nested flex (not `h-full`) sizes the viewport: percentage heights
    // don't resolve inside a max-height-capped flex parent, but flex
    // shrinking with min-h-0 does.
    <div className={cn('relative flex min-h-0 flex-col', className)}>
      <div
        ref={viewportRef}
        // `scroll-area-viewport` opts this element out of the global
        // overlay-scrollbar module so it doesn't get a second thumb.
        className={cn('scroll-area-viewport min-h-0 flex-1 overflow-y-auto', viewportClassName)}
      >
        {children}
      </div>
      {thumb ? (
        <div
          aria-hidden="true"
          className="absolute right-0 w-[5px] rounded-full"
          style={{ top: `${thumb.top}px`, height: `${thumb.height}px`, backgroundColor: thumbColor }}
          onPointerDown={handleThumbPointerDown}
        />
      ) : null}
    </div>
  );
}

export { ScrollArea };
